<template>
  <v-dialog
      v-model="dialog"
      max-width="900"
      persistent
      scrollable
  >
    <v-card class="location-geo-fix-modal">
      <v-card-title>
        <v-icon left>mdi-map-marker-check</v-icon>
        {{ $t('location.geolocation-check.fix-position') }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-5">
        <v-row>
          <!-- Colonne gauche : Adresse -->
          <v-col cols="5">
            <div class="subtitle-1 font-weight-bold mb-3">{{ $t('address') }}</div>

            <v-row dense>
              <v-col cols="3">
                <v-text-field
                    :label="$t('number')"
                    v-model="localLocation.address.number"
                    dense
                    outlined
                    hide-details
                />
              </v-col>
              <v-col cols="9">
                <v-text-field
                    :label="$t('street')"
                    v-model="localLocation.address.street"
                    dense
                    outlined
                    hide-details
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="4">
                <v-text-field
                    :label="$t('zipcode')"
                    v-model="localLocation.address.zipcode"
                    dense
                    outlined
                    hide-details
                />
              </v-col>
              <v-col cols="8">
                <v-text-field
                    :label="$t('city')"
                    v-model="localLocation.address.city"
                    dense
                    outlined
                    hide-details
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                    :label="$t('state')"
                    v-model="localLocation.address.state"
                    dense
                    outlined
                    hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                    :label="$t('country')"
                    v-model="localLocation.address.country"
                    dense
                    outlined
                    hide-details
                />
              </v-col>
            </v-row>

            <v-btn
                class="mt-4"
                color="primary"
                block
                small
                @click="searchAddress"
                :loading="searching"
            >
              <v-icon left>mdi-magnify</v-icon>
              {{ $t('location.geolocation-check.search-address') }}
            </v-btn>
          </v-col>

          <!-- Colonne droite : Carte -->
          <v-col cols="7">
            <div class="map-container" v-if="showMap">
              <Map
                  ref="map-container"
                  :zoom="15"
                  @move="centerMarker"
                  :on-map-ready="onMapReady"
              >
                <l-marker
                    v-if="marker"
                    :icon="selectedIcon"
                    :lat-lng="marker"
                />
              </Map>
            </div>
            <div class="map-container map-placeholder" v-else>
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <v-row dense class="mt-2">
              <v-col>
                <v-text-field
                    :label="$t('latitude')"
                    type="number"
                    v-model.number="localLocation.coordinates.latitude"
                    dense
                    outlined
                    hide-details
                    readonly
                />
              </v-col>
              <v-col>
                <v-text-field
                    :label="$t('longitude')"
                    type="number"
                    v-model.number="localLocation.coordinates.longitude"
                    dense
                    outlined
                    hide-details
                    readonly
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Suggestions d'adresses -->
        <v-row v-if="addresses.length > 0" class="mt-4">
          <v-col>
            <div class="subtitle-1 font-weight-bold mb-2">{{ $t('location.geolocation-check.select-address') }}</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t('address') }}</th>
                    <th width="120"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in addresses" :key="index">
                    <td>
                      {{ item.address }}<template v-if="item.location">, {{ item.location }}</template>
                    </td>
                    <td>
                      <v-btn
                          x-small
                          color="primary"
                          :loading="selecting === index"
                          @click="setAddress(item, index)"
                      >
                        {{ $t('define') }}
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>

        <v-alert v-if="noResults" type="warning" class="mt-4" dense>
          {{ $t('location.geolocation-check.no-address-found') }}
        </v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn small text @click="close">
          {{ $t('close') }}
        </v-btn>
        <v-btn small color="primary" :loading="saving" @click="save">
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Map from "@blocks/Map.vue";
import { Icon, LatLng } from "leaflet";
import { LMarker } from "vue2-leaflet";
import iconSelected from "@images/marker-selected.svg";
import iconShadow from "@images/marker-shadow.png";
import LocationRepository from "@repository/LocationRepository";
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'LocationGeoFixModal',

  components: {
    Map,
    LMarker,
  },

  props: {
    location: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      dialog: false,
      localLocation: null,
      marker: null,
      showMap: false,
      mapReady: false,
      searching: false,
      selecting: null,
      saving: false,
      addresses: [],
      noResults: false,
      sessionId: null,
      selectedIcon: new Icon({
        iconUrl: iconSelected,
        shadowUrl: iconShadow,
        iconSize: [50, 50],
        iconAnchor: [25, 50],
      }),
    };
  },

  methods: {
    open() {
      if (!this.location) return;

      this.initLocalLocation(this.location);
      this.addresses = [];
      this.noResults = false;
      this.showMap = false;
      this.mapReady = false;
      this.sessionId = uuidv4();
      this.dialog = true;

      // Show map after dialog transition is complete (300ms is typical v-dialog transition)
      setTimeout(() => {
        this.showMap = true;
        this.searchAddress();
      }, 350);
    },

    close() {
      this.showMap = false;
      this.dialog = false;
    },

    onMapReady(map) {
      // Force Leaflet to recalculate container size
      setTimeout(() => {
        map.invalidateSize();
      }, 100);

      if (this.localLocation?.coordinates?.latitude && this.localLocation?.coordinates?.longitude) {
        const center = new LatLng(
            this.localLocation.coordinates.latitude,
            this.localLocation.coordinates.longitude
        );
        map.panTo(center);
        this.marker = center;
      }
      this.$nextTick(() => {
        this.mapReady = true;
      });
    },

    centerMarker() {
      if (this.mapReady) {
        const map = this.$refs['map-container'].$refs.map.mapObject;
        const center = map.getCenter();
        this.marker = center;
        this.localLocation.coordinates.latitude = center.lat;
        this.localLocation.coordinates.longitude = center.lng;
      }
    },

    buildAddressQuery() {
      const parts = [];
      if (this.localLocation.address?.number) parts.push(this.localLocation.address.number);
      if (this.localLocation.address?.street) parts.push(this.localLocation.address.street);
      if (this.localLocation.address?.city) parts.push(this.localLocation.address.city);
      if (this.localLocation.address?.zipcode) parts.push(this.localLocation.address.zipcode);
      if (this.localLocation.address?.country) parts.push(this.localLocation.address.country);
      return parts.join(' ');
    },

    async searchAddress() {
      const query = this.buildAddressQuery();
      if (!query || query.length < 3) {
        this.noResults = true;
        return;
      }

      this.searching = true;
      this.addresses = [];
      this.noResults = false;

      try {
        const results = await LocationRepository.searchAddress(query, this.sessionId, this.localLocation.id);
        if (results && results.length > 0) {
          this.addresses = results;
        } else {
          this.noResults = true;
        }
      } catch (error) {
        console.error('Error searching address:', error);
        this.noResults = true;
      } finally {
        this.searching = false;
      }
    },

    async setAddress(item, index) {
      this.selecting = index;

      try {
        const result = await LocationRepository.getPlace(item.placeId, this.sessionId);

        if (result?.coordinates) {
          // Update coordinates
          this.localLocation.coordinates.latitude = result.coordinates.lat;
          this.localLocation.coordinates.longitude = result.coordinates.lng;

          // Update map
          const position = new LatLng(result.coordinates.lat, result.coordinates.lng);
          this.marker = position;
          if (this.$refs['map-container']?.$refs?.map?.mapObject) {
            this.$refs['map-container'].$refs.map.mapObject.panTo(position);
          }
        }
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.selecting = null;
        this.sessionId = uuidv4();
      }
    },

    async save() {
      this.saving = true;

      try {
        const data = this.cloneObj(this.localLocation);
        data.coordinatesIsChecked = true
        const response = await LocationRepository.update(this.localLocation.id, data);

        this.initLocalLocation(response);
        this.$emit('update:location', this.localLocation);
        this.$dialog.notify.success(this.$t('location.geolocation-check.corrected'));
        this.close();
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.saving = false;
      }
    },

    initLocalLocation(location) {
      if (location) {
        const localLocation = this.cloneObj(location);
        if (!localLocation.organizationId) {
          localLocation.organizationId = localLocation.organization?.id;
        }
        if (!localLocation.coordinates) {
          localLocation.coordinates = {
            latitude: null,
            longitude: null,
          };
        }
        if (!localLocation.address) {
          localLocation.address = {};
        }
        if (!localLocation.timezone) {
          localLocation.timezone = null;
        }
        this.localLocation = localLocation;
      }
    },
  },
};
</script>

<style lang="scss">
.location-geo-fix-modal {
  .map-container {
    height: 300px;
    border: 2px solid #E53C22;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .map-block {
      flex: 1;
      min-height: 0;
    }

    &.map-placeholder {
      align-items: center;
      justify-content: center;
      background-color: #f5f5f5;
    }
  }

  .v-simple-table {
    border: 1px solid #e0e0e0;
    border-radius: 4px;

    tbody tr:hover {
      background-color: #f5f5f5;
    }
  }
}
</style>
